import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useModalStore } from "../../../../global-account/react/index.js";
import { getAuthToken } from "../../../../shared/utils/auth-token.js";
import { debugB3React } from "../../../../shared/utils/debug.js";
import { useCallback, useEffect, useState } from "react";
import { useUserQuery } from "../../hooks/useUserQuery.js";
import { notificationsAPI } from "../../utils/notificationsAPI.js";
import ModalHeader from "../ModalHeader/ModalHeader.js";
import { toast } from "../Toast/toastApi.js";
import { DiscordChannel, EmailChannel, PhoneChannel, TelegramChannel } from "./channels/index.js";
const debug = debugB3React("NotificationsContent");
const NotificationsContent = ({
  partnerId,
  chain,
  onSuccess
}) => {
  const setB3ModalContentType = useModalStore(state => state.setB3ModalContentType);
  const setB3ModalOpen = useModalStore(state_0 => state_0.setB3ModalOpen);
  const {
    user
  } = useUserQuery();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  // Optimistic UI state
  const [optimisticChannelStates, setOptimisticChannelStates] = useState({});
  const userId = user?.userId;
  const jwtToken = getAuthToken();
  const fetchUserData = useCallback(async (showLoading = true) => {
    if (!userId || !jwtToken) {
      if (showLoading) setLoading(false);
      return;
    }
    try {
      if (showLoading) setLoading(true);
      const data_0 = await notificationsAPI.getUser(userId, jwtToken);
      setUserData(data_0);
      setError(null);
    } catch (err) {
      debug("Error loading user data:", err);
      // Try to register the user if they don't exist
      try {
        await notificationsAPI.registerUser(jwtToken);
        const data = await notificationsAPI.getUser(userId, jwtToken);
        setUserData(data);
        setError(null);
      } catch (registerErr) {
        debug("Error registering user:", registerErr);
        setError("Failed to load notification settings");
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [userId, jwtToken]);
  useEffect(() => {
    fetchUserData(true);
  }, [fetchUserData]);
  const handleConnectionChange = () => {
    fetchUserData(false); // Refresh without showing loading screen
    onSuccess?.();
  };
  const handleToggleChannel = async (channelType, shouldDisconnect) => {
    if (!userId || !jwtToken) return;
    if (shouldDisconnect) {
      // Optimistically update UI
      setOptimisticChannelStates(prev => ({
        ...prev,
        [channelType]: false
      }));
      try {
        await notificationsAPI.disconnectChannel(userId, channelType, jwtToken);
        await fetchUserData(false); // Refresh without showing loading screen
        setOptimisticChannelStates(prev_1 => {
          const newState_0 = {
            ...prev_1
          };
          delete newState_0[channelType];
          return newState_0;
        });
        toast.success(`${channelType.charAt(0).toUpperCase() + channelType.slice(1)} disconnected successfully!`);
      } catch (err_0) {
        debug("Error disconnecting channel:", err_0);
        setError(`Failed to disconnect ${channelType}`);
        toast.error(`Failed to disconnect ${channelType}`);
        // Revert optimistic update
        setOptimisticChannelStates(prev_0 => {
          const newState = {
            ...prev_0
          };
          delete newState[channelType];
          return newState;
        });
      }
    }
  };
  const handleSendTestNotification = async () => {
    if (!userId || !jwtToken) return;
    try {
      setIsSendingTest(true);
      setTestSuccess(false);
      await notificationsAPI.sendTestNotification(userId, partnerId, jwtToken);
      setTestSuccess(true);
      setTimeout(() => setTestSuccess(false), 5000);
    } catch (err_1) {
      debug("Error sending test notification:", err_1);
      setError("Failed to send test notification. Make sure you have at least one channel connected.");
    } finally {
      setIsSendingTest(false);
    }
  };
  const handleBack = () => {
    setB3ModalContentType({
      type: "manageAccount",
      chain,
      partnerId,
      activeTab: "settings"
    });
    setB3ModalOpen(true);
  };
  const emailChannel = userData?.channels?.find(c => c.channel_type === "email");
  const smsChannel = userData?.channels?.find(c_0 => c_0.channel_type === "sms");
  const whatsappChannel = userData?.channels?.find(c_1 => c_1.channel_type === "whatsapp");
  const telegramChannel = userData?.channels?.find(c_2 => c_2.channel_type === "telegram");
  const discordChannel = userData?.channels?.find(c_3 => c_3.channel_type === "discord");
  // Real connection states
  const emailConnected = emailChannel?.enabled === 1;
  const smsConnected = smsChannel?.enabled === 1;
  const whatsappConnected = whatsappChannel?.enabled === 1;
  const telegramConnected = telegramChannel?.enabled === 1;
  const discordConnected = discordChannel?.enabled === 1;
  // Apply optimistic updates
  const optimisticEmailConnected = optimisticChannelStates["email"] !== undefined ? optimisticChannelStates["email"] : emailConnected;
  const optimisticSMSConnected = optimisticChannelStates["sms"] !== undefined ? optimisticChannelStates["sms"] : smsConnected;
  const optimisticWhatsAppConnected = optimisticChannelStates["whatsapp"] !== undefined ? optimisticChannelStates["whatsapp"] : whatsappConnected;
  const optimisticTelegramConnected = optimisticChannelStates["telegram"] !== undefined ? optimisticChannelStates["telegram"] : telegramConnected;
  const optimisticDiscordConnected = optimisticChannelStates["discord"] !== undefined ? optimisticChannelStates["discord"] : discordConnected;
  const hasAnyChannelConnected = optimisticEmailConnected || optimisticSMSConnected || optimisticWhatsAppConnected || optimisticTelegramConnected || optimisticDiscordConnected;
  if (loading) {
    return _jsxs("div", {
      className: "flex h-[470px] flex-col",
      children: [_jsx(ModalHeader, {
        showBackButton: true,
        showCloseButton: false,
        title: "Notifications",
        handleBack: handleBack
      }), _jsx("div", {
        className: "flex flex-1 items-center justify-center",
        children: _jsx("div", {
          className: "font-['PP_Neue_Montreal','PP_Neue_Montreal_Fallback',sans-serif] text-[14px] leading-[20px] text-gray-500",
          children: "Loading..."
        })
      })]
    });
  }
  return _jsxs("div", {
    className: "flex h-[470px] flex-col",
    children: [_jsx(ModalHeader, {
      showBackButton: true,
      showCloseButton: false,
      title: "Notifications",
      handleBack: handleBack
    }), _jsxs("div", {
      className: "flex-1 space-y-2 overflow-y-auto px-5 py-6",
      children: [error && _jsx("div", {
        className: "mb-3 rounded-lg bg-red-50 p-3 font-['PP_Neue_Montreal','PP_Neue_Montreal_Fallback',sans-serif] text-[14px] leading-[20px] text-red-600 dark:bg-red-900/20 dark:text-red-400",
        children: error
      }), userId && jwtToken && _jsxs("div", {
        className: "b3-modal-notifications-channels dark:bg-b3-background rounded-[20px] bg-[#f4f4f5]",
        children: [_jsx(PhoneChannel, {
          userId: userId,
          jwtToken: jwtToken,
          smsChannel: smsChannel,
          whatsappChannel: whatsappChannel,
          isSMSConnected: smsConnected,
          isWhatsAppConnected: whatsappConnected,
          isOptimisticallySMSConnected: optimisticSMSConnected,
          isOptimisticallyWhatsAppConnected: optimisticWhatsAppConnected,
          onConnectionChange: handleConnectionChange,
          onToggle: (type, shouldDisconnect_0) => handleToggleChannel(type, shouldDisconnect_0)
        }), _jsx(EmailChannel, {
          userId: userId,
          jwtToken: jwtToken,
          emailChannel: emailChannel,
          isConnected: emailConnected,
          isOptimisticallyConnected: optimisticEmailConnected,
          onConnectionChange: handleConnectionChange,
          onToggle: enabled => handleToggleChannel("email", enabled)
        }), _jsx(TelegramChannel, {
          userId: userId,
          jwtToken: jwtToken,
          telegramChannel: telegramChannel,
          isConnected: telegramConnected,
          isOptimisticallyConnected: optimisticTelegramConnected,
          onConnectionChange: handleConnectionChange,
          onToggle: enabled_0 => handleToggleChannel("telegram", enabled_0)
        }), _jsx(DiscordChannel, {
          userId: userId,
          jwtToken: jwtToken,
          discordChannel: discordChannel,
          isConnected: discordConnected,
          isOptimisticallyConnected: optimisticDiscordConnected,
          onConnectionChange: handleConnectionChange,
          onToggle: enabled_1 => handleToggleChannel("discord", enabled_1)
        })]
      }), process.env.NODE_ENV === "development" && hasAnyChannelConnected && _jsxs("div", {
        className: "mt-6 rounded-xl border border-[#e4e4e7] bg-white p-4",
        children: [_jsxs("div", {
          className: "mb-3 flex items-center gap-2",
          children: [_jsx("div", {
            className: "text-lg",
            children: "\uD83E\uDDEA"
          }), _jsx("h3", {
            className: "font-['PP_Neue_Montreal','PP_Neue_Montreal_Fallback',sans-serif] text-[14px] font-semibold leading-[20px] text-gray-900",
            children: "Test Notifications"
          })]
        }), _jsx("p", {
          className: "mb-3 font-['PP_Neue_Montreal','PP_Neue_Montreal_Fallback',sans-serif] text-[14px] leading-[20px] text-gray-600",
          children: "Send a test notification to all your connected channels to verify they're working correctly."
        }), _jsx("button", {
          onClick: handleSendTestNotification,
          disabled: isSendingTest,
          className: "w-full rounded-lg bg-purple-600 px-4 py-2 font-['Inter',sans-serif] text-[14px] font-semibold leading-[20px] text-white transition-colors hover:bg-purple-700 disabled:opacity-50",
          children: isSendingTest ? "Sending..." : "Send Test Notification"
        }), testSuccess && _jsx("div", {
          className: "mt-3 rounded-lg bg-green-50 p-3 font-['PP_Neue_Montreal','PP_Neue_Montreal_Fallback',sans-serif] text-[14px] leading-[20px] text-green-700",
          children: "\u2713 Test notification sent! Check your connected channels."
        })]
      })]
    })]
  });
};
export default NotificationsContent;